import { removeStagingObject } from "./Api";

// Autosaved new-invoice draft (localStorage), so a session timeout, crash or
// accidental navigation never loses in-progress work. Mirrors the SOA draft flow.
//
// v2: the vendor's invoice number moved from each property section to the form
// root. That is the only shape difference, so a v1 draft is MIGRATED into the v2
// key rather than thrown away — it is an invoice the user never got to submit,
// and the only thing allowed to destroy it (and its staged uploads) is the user
// choosing Discard on the restore prompt.
const INVOICE_DRAFT_KEY = "invoice-form-draft-v2";
const LEGACY_DRAFT_KEYS = ["invoice-form-draft-v1"];

// The staged S3 objects a draft is holding open. Uploads happen before the
// invoice exists, so a draft that is thrown away has to take its uploads with it.
const stagedKeysOf = (draft) => [
  ...(draft?.pendingFiles || []).map((f) => f?.key),
  ...((draft?.form?.groups || []).flatMap((g) =>
    (g.rows || []).flatMap((r) => (r.files || []).map((f) => f?.key))
  )),
].filter(Boolean);

// localStorage can throw (private mode, quota, disabled). Every access here is
// best-effort and silent: none of it is something the user asked for.
const readKey = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; /* storage unavailable — nothing we can do */
  }
};

const writeKey = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false; /* storage full or unavailable */
  }
};

const removeKey = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

// v1 auto-filled each section's number with the RD number that section's first
// line was predicted to receive. That is this system's own number; v2's single
// field is the VENDOR's own number, so a leftover prediction is dropped instead
// of being carried into a field it does not belong in.
const AUTO_REF_PREDICTION = /^RD\s*\d+$/i;

// v1 -> v2: per-section referenceNo becomes one invoice-level value. Take the
// first number the user actually typed — the same rule applySuggestion uses for
// the extractor's per-section numbers, since one paper invoice carries one.
const migrateLegacyDraft = (draft) => {
  const groups = draft?.form?.groups || [];
  const typed = groups
    .map((g) => String(g?.referenceNo || "").trim())
    .find((ref) => ref && !AUTO_REF_PREDICTION.test(ref)) || "";
  return {
    ...draft,
    form: {
      ...draft.form,
      referenceNo: String(draft?.form?.referenceNo || "").trim() || typed,
      groups: groups.map((g) => {
        const next = { ...g };
        delete next.referenceNo;
        return next;
      }),
    },
  };
};

// Move a v1 draft under the v2 key so the rest of the flow (restore prompt,
// autosave, discard) only ever sees one draft. Nothing here deletes a draft or
// a staged upload: an unmigratable draft is left exactly where it is.
const migrateLegacyDrafts = () => {
  for (const key of LEGACY_DRAFT_KEYS) {
    const raw = readKey(key);
    if (!raw) continue;
    let legacy = null;
    try {
      legacy = JSON.parse(raw);
    } catch {
      continue; /* unreadable — leave it in place rather than destroy it */
    }
    if (!legacy?.form) continue;
    const migrated = migrateLegacyDraft(legacy);
    // Drop the old key only once the new one is safely written; if the write
    // failed the draft still restores from memory this time round and the
    // migration is retried on the next read.
    if (writeKey(INVOICE_DRAFT_KEY, JSON.stringify(migrated))) removeKey(key);
    return migrated;
  }
  return null;
};

export const readInvoiceDraft = () => {
  const raw = readKey(INVOICE_DRAFT_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  // No v2 draft — a v1 one is still the user's unsubmitted invoice.
  return migrateLegacyDrafts();
};

export const writeInvoiceDraft = (draft) => {
  try {
    writeKey(INVOICE_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* unserialisable draft — autosave is best-effort */
  }
};

export const clearInvoiceDraft = () => {
  removeKey(INVOICE_DRAFT_KEY);
};

// Discard = clear the draft AND delete the staged S3 uploads it references.
// The only path that throws work away, and it runs on an explicit user choice.
export const discardInvoiceDraft = () => {
  const draft = readInvoiceDraft();
  if (draft) {
    stagedKeysOf(draft).forEach((k) => removeStagingObject(k).catch(() => {}));
  }
  clearInvoiceDraft();
  // A migration that could not write (storage full) leaves the v1 key behind;
  // the user has just said discard, so it goes too.
  LEGACY_DRAFT_KEYS.forEach(removeKey);
};
